import type { AgentSpec } from './agent-spec';
import { bobcatApiUrl } from './bobcat-api';

/**
 * Project Bobcat — `POST /ai/agents/draft`, served by the backend
 * (`bobcatApiUrl`): the AI draft of one use-case agent, for a task Create
 * found no template for.
 */

/** The route's wire shapes; change them together with the backend's. */
export interface DraftAgentRequest {
  /** The agent's name, as Create named it. */
  name: string;
  /** What customers say to reach it. */
  phrases: string[];
  /** The solution's brief and global instructions. */
  brief: string;
  /** Commands the solution already has (scripts and system commands). */
  commands: string[];
}

export interface DraftAgentResult {
  summary: string;
  spec: AgentSpec;
}

/** A little under the server function's 60s limit, as the assistant does. */
const TIMEOUT_MS = 55_000;

export const requestAgentDraft = async (
  body: DraftAgentRequest
): Promise<DraftAgentResult> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const response = await fetch(bobcatApiUrl('/ai/agents/draft'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    const json = await response.json().catch(() => null);
    if (!response.ok) {
      const b = json as { detail?: string; error?: string } | null;
      throw new Error(b?.detail ?? b?.error ?? response.statusText);
    }
    return json as DraftAgentResult;
  } finally {
    clearTimeout(timer);
  }
};
